/* =========================================================
   カルーセル共通スクリプト（ピックアップ／人気商品で共用）

   ・スワイプ／慣性スクロールはブラウザネイティブに任せる（JSは介入しない）
   ・PCの左右ボタンは requestAnimationFrame で自前にスクロールを補間する
       ブラウザは OS の「モーション軽減」設定が有効なとき
       scroll-behavior: smooth / behavior: 'smooth' を無視して瞬間移動させるため、
       ボタン送りがカクついて見える。自前補間ならその影響を受けない。
       補間中は scroll-snap が干渉しないよう一時的に snap を切る。
   ・data-loop を付けると無限ループになる
       前後に1セットずつ複製を挿入し、実セットの位置から開始する。
       端に近づいたら1セット分だけ位置を瞬間移動させる（見た目は同一のため継ぎ目が出ない）。
       これにより「最後の次にまた最初が出る」動きと、左右両側の見切れ表示を両立する。
   ・カスタム要素として実装しているため、テーマエディタでセクションが
     再読み込みされると connectedCallback が再実行され自動的に再初期化される

   使い方：
     <chk-carousel data-loop>
       <button data-carousel-prev>…</button>
       <ul data-carousel-track> <li>…</li> </ul>
       <button data-carousel-next>…</button>
     </chk-carousel>
   ========================================================= */

(function () {
  if (!window.customElements || customElements.get('chk-carousel')) return;

  customElements.define(
    'chk-carousel',
    class ChkCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-carousel-track]');
        this.prevButton = this.querySelector('[data-carousel-prev]');
        this.nextButton = this.querySelector('[data-carousel-next]');
        this.loop = this.hasAttribute('data-loop');
        this.isAdjusting = false;
        this.isAnimating = false;
        this.rafId = null;
        this.settleTimer = null;

        if (!this.track) return;

        this._onPrev = this.scrollToDirection.bind(this, -1);
        this._onNext = this.scrollToDirection.bind(this, 1);
        this._onScroll = this.handleScroll.bind(this);
        this._onResize = this.handleResize.bind(this);

        if (this.prevButton) this.prevButton.addEventListener('click', this._onPrev);
        if (this.nextButton) this.nextButton.addEventListener('click', this._onNext);
        this.track.addEventListener('scroll', this._onScroll, { passive: true });
        window.addEventListener('resize', this._onResize);

        // scrollend が使えるブラウザでは、慣性スクロールが完全に止まってから
        // ループ位置を補正する（スワイプ中の割り込みが無くなり動きが滑らかになる）。
        this.hasScrollEnd = 'onscrollend' in window;
        if (this.hasScrollEnd) {
          this._onScrollEnd = this.recenter.bind(this);
          this.track.addEventListener('scrollend', this._onScrollEnd);
        }

        if (this.loop) this.setupLoop();

        this.setupArrowAlign();

        // レイアウト確定後に開始位置とボタンの活性状態を反映する
        window.requestAnimationFrame(() => {
          this.lastWidth = this.track.clientWidth;
          if (this.loop) this.goToStart();
          this.updateButtons();
        });
      }

      disconnectedCallback() {
        if (this.prevButton) this.prevButton.removeEventListener('click', this._onPrev);
        if (this.nextButton) this.nextButton.removeEventListener('click', this._onNext);
        if (this.track) this.track.removeEventListener('scroll', this._onScroll);
        if (this.track && this._onScrollEnd) this.track.removeEventListener('scrollend', this._onScrollEnd);
        window.removeEventListener('resize', this._onResize);
        window.clearTimeout(this.settleTimer);
        if (this.rafId) window.cancelAnimationFrame(this.rafId);
        if (this.mediaObserver) this.mediaObserver.disconnect();
      }

      /* ---------- 左右ボタンを画像の縦中央に合わせる ---------- */

      // data-arrow-media="<セレクタ>" が付いているときだけ動く。
      // 指定要素（画像領域）の高さを実測し、その半分を --chk-arrow-center に入れる。
      // CSS側は top: var(--chk-arrow-center, <従来値>) で受け取るため、
      // 属性が無い／要素が見つからない場合は従来の見た目のまま変わらない。
      //
      // 割合（top: 40% 等）ではなく実測にしている理由：
      //   ・カード内のテキストが1行か2行かでカルーセルの高さが変わる
      //   ・商品カードの画像比率が商品ごとに変わる（image_ratio: "adapt"）
      // いずれの場合も割合指定では中央からずれてしまう。
      setupArrowAlign() {
        const selector = this.getAttribute('data-arrow-media');
        if (!selector) return;

        const media = this.track.querySelector(selector);
        if (!media) return;

        this.updateArrowCenter = () => {
          const height = media.getBoundingClientRect().height;
          // 画像の読み込み前など高さ0のときは書き換えない（CSSの既定値を残す）
          if (height > 0) this.style.setProperty('--chk-arrow-center', height / 2 + 'px');
        };

        this.updateArrowCenter();

        // 画像の読み込み完了・ウィンドウ幅の変化・比率の異なる商品への差し替えに追従する
        if (window.ResizeObserver) {
          this.mediaObserver = new ResizeObserver(this.updateArrowCenter);
          this.mediaObserver.observe(media);
        }
      }

      /* ---------- ループ用の複製 ---------- */

      setupLoop() {
        const items = Array.prototype.slice.call(this.track.children);
        this.realCount = items.length;

        // 1枚しかない場合はループさせない
        if (this.realCount < 2) {
          this.loop = false;
          return;
        }

        const before = document.createDocumentFragment();
        const after = document.createDocumentFragment();

        items.forEach((item) => {
          [before, after].forEach((fragment) => {
            const clone = item.cloneNode(true);
            clone.setAttribute('aria-hidden', 'true');
            clone.setAttribute('data-carousel-clone', '');
            // 複製はタブ順・読み上げから外す
            clone.querySelectorAll('a, button, input, select, textarea').forEach((el) => {
              el.setAttribute('tabindex', '-1');
            });
            fragment.appendChild(clone);
          });
        });

        this.track.insertBefore(before, this.track.firstChild);
        this.track.appendChild(after);
      }

      /* ---------- 位置の計算 ---------- */

      // スライド1枚分（＋間隔）
      slideStep() {
        const item = this.track.firstElementChild;
        if (!item) return this.track.clientWidth;

        const styles = window.getComputedStyle(this.track);
        const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return item.getBoundingClientRect().width + gap;
      }

      // トラック内での要素の左端位置（スクロール量を含む絶対位置）。
      // offsetLeft は整数に丸められるため、スライド幅が小数になる構成（％＋calc）では
      // ループ位置の補正に誤差が蓄積する。実測値から求めて丸めを避ける。
      offsetOf(item) {
        const trackLeft = this.track.getBoundingClientRect().left;
        return item.getBoundingClientRect().left - trackLeft + this.track.scrollLeft;
      }

      // 1セット分の幅。同じスライドが1セット後に来る距離を実測する。
      setWidth() {
        const items = this.track.children;
        if (items.length <= this.realCount) return this.slideStep() * this.realCount;
        return this.offsetOf(items[this.realCount]) - this.offsetOf(items[0]);
      }

      // 指定要素が snap する位置
      //   center 揃え … 表示領域の中央に合わせる
      //   start 揃え  … 左端に合わせる。ただし scroll-padding-inline-start を確保して
      //                 左側にも見切れが出るようにする（ブラウザの snap 位置と一致させる）
      snapPositionOf(item) {
        if (!item) return 0;

        const align = window.getComputedStyle(item).scrollSnapAlign || '';
        const itemWidth = item.getBoundingClientRect().width;

        if (align.indexOf('center') !== -1) {
          return this.offsetOf(item) - (this.track.clientWidth - itemWidth) / 2;
        }

        const scrollPaddingLeft = parseFloat(window.getComputedStyle(this.track).scrollPaddingLeft) || 0;
        return this.offsetOf(item) - scrollPaddingLeft;
      }

      // 実セットの1枚目へ（アニメーションなし）
      goToStart() {
        const first = this.track.children[this.realCount];
        this.jumpTo(this.snapPositionOf(first));
      }

      // アニメーションなしで位置を移動する。
      // scroll-snap が有効なままだと、代入した位置をブラウザが引き戻すことがあるため
      // 一時的に snap を切ってから移動する。
      jumpTo(position) {
        this.isAdjusting = true;

        const previousBehavior = this.track.style.scrollBehavior;
        const previousSnap = this.track.style.scrollSnapType;

        this.track.style.scrollBehavior = 'auto';
        this.track.style.scrollSnapType = 'none';
        this.track.scrollLeft = position;
        this.track.style.scrollSnapType = previousSnap || '';
        this.track.style.scrollBehavior = previousBehavior || '';

        // 直後の scroll イベントを無視するため、次フレームで解除する
        window.requestAnimationFrame(() => {
          this.isAdjusting = false;
        });
      }

      /* ---------- 操作 ---------- */

      scrollToDirection(direction) {
        this.animateScrollTo(this.track.scrollLeft + this.slideStep() * direction);
      }

      // requestAnimationFrame でスクロール位置を補間する（ブラウザの smooth に依存しない）
      animateScrollTo(target, duration) {
        const track = this.track;
        const start = track.scrollLeft;
        const distance = target - start;

        if (this.rafId) window.cancelAnimationFrame(this.rafId);
        if (Math.abs(distance) < 1) return;

        const total = duration || 450;
        const startTime = window.performance.now();

        // 補間中は snap が引っ張り合うため一時的に無効化する
        track.style.scrollSnapType = 'none';
        this.isAnimating = true;

        const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

        const step = (now) => {
          const progress = Math.min((now - startTime) / total, 1);
          track.scrollLeft = start + distance * easeOutCubic(progress);

          if (progress < 1) {
            this.rafId = window.requestAnimationFrame(step);
            return;
          }

          this.rafId = null;
          this.isAnimating = false;
          // ループ位置の補正は snap を切ったまま先に行う。
          // 先に snap を戻すと、補正前の位置へ引き戻されて動きが乱れるため。
          if (this.loop) this.recenter();
          track.style.scrollSnapType = '';
          this.updateButtons();
        };

        this.rafId = window.requestAnimationFrame(step);
      }

      handleScroll() {
        // 自前補間中はループ補正を挟まない（動きが途切れるため）
        if (this.isAdjusting || this.isAnimating) return;

        // scrollend が無いブラウザ向けのフォールバック。
        // スクロールが落ち着いてから位置を補正する（慣性スクロール中の割り込みを避ける）
        if (this.loop && !this.hasScrollEnd) {
          window.clearTimeout(this.settleTimer);
          this.settleTimer = window.setTimeout(this.recenter.bind(this), 180);
        }

        this.updateButtons();
      }

      handleResize() {
        // ResizeObserver が使えない環境向けのフォールバック（通常はRO側で追従する）
        if (this.updateArrowCenter && !this.mediaObserver) this.updateArrowCenter();

        // スマホでは上下スクロールでアドレスバーが伸縮するだけでも resize が発火する。
        // 毎回先頭へ戻すと操作中に勝手に巻き戻るため、横幅が実際に変わったときだけ
        // 位置を取り直す。
        const width = this.track.clientWidth;
        if (width === this.lastWidth) {
          this.updateButtons();
          return;
        }

        this.lastWidth = width;
        if (this.loop) this.goToStart();
        this.updateButtons();
      }

      // 複製セット側まで進んでいたら、見た目が同じ実セット側の位置へ瞬間移動する
      recenter() {
        const set = this.setWidth();
        if (set <= 0) return;

        const current = this.track.scrollLeft;
        if (current < set * 0.5) {
          this.jumpTo(current + set);
        } else if (current > set * 1.5) {
          this.jumpTo(current - set);
        }
      }

      // 端に到達したらボタンを disabled にする（ループ時は常に操作可）
      updateButtons() {
        if (!this.prevButton || !this.nextButton) return;

        if (this.loop) {
          this.prevButton.disabled = false;
          this.nextButton.disabled = false;
          return;
        }

        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        // 小数点誤差を吸収するための許容値
        const tolerance = 2;

        this.prevButton.disabled = this.track.scrollLeft <= tolerance;
        this.nextButton.disabled = this.track.scrollLeft >= maxScroll - tolerance;
      }
    }
  );
})();
